import React, { useRef, useState, useEffect } from 'react';
import { usePermissions, Permission } from './usePermissions';
import { checkFeatureLimit, getFeatureLimitInfo, subscribeToDataChanges } from '../limits';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown } from 'lucide-react';
import { cn, Button } from '@nimbitlabs/ui';

interface PermissionGateProps {
  children: React.ReactNode;
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
  domain?: string; // Generic domain name (e.g., 'services', 'teams', 'environments', 'users')
  isCreate?: boolean;
  showLocked?: boolean;
  fallback?: React.ReactNode;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
}

export function PermissionGate({
  children,
  permission,
  permissions,
  requireAll = false,
  domain,
  isCreate = false,
  showLocked = true,
  fallback,
  tooltipPosition = 'bottom',
}: PermissionGateProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useState({});
  
  // Parse domain and operation from permission string if not explicitly provided
  // Use useMemo to prevent recalculation on every render
  const { effectiveDomain, effectiveIsCreate } = React.useMemo(() => {
    let parsedDomain = domain;
    let parsedIsCreate = isCreate;
    
    if (permission && !domain) {
      // Parse "teams:create" -> domain: "teams", operation: "create"
      const parts = permission.split(':');
      if (parts.length === 2) {
        parsedDomain = parts[0];
        parsedIsCreate = parts[1] === 'create';
      }
    }
    
    return { effectiveDomain: parsedDomain, effectiveIsCreate: parsedIsCreate };
  }, [permission, domain, isCreate]);
  
  // Subscribe to data changes for reactive limit checking
  useEffect(() => {
    if (effectiveIsCreate && effectiveDomain) {
      const unsubscribe = subscribeToDataChanges(effectiveDomain, () => {
        // Force re-render when data changes
        forceUpdate({});
      });
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [effectiveIsCreate, effectiveDomain]);
  
  // Check permission
  let hasRequiredPermission = true;
  if (permission) {
    hasRequiredPermission = hasPermission(permission);
  } else if (permissions) {
    hasRequiredPermission = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }
  
  // Check subscription limit if creating
  let subscriptionLimitReached = false;
  let limitInfo: { current: number; limit: number | null; hasReachedLimit: boolean } | null = null;
  
  if (effectiveIsCreate && effectiveDomain) {
    subscriptionLimitReached = checkFeatureLimit(effectiveDomain);
    limitInfo = getFeatureLimitInfo(effectiveDomain);
  }
  
  const shouldShowLocked = !hasRequiredPermission || subscriptionLimitReached;
  
  // Determine the message to show
  let lockedMessage = '';
  if (subscriptionLimitReached) {
    lockedMessage = `You've reached your ${effectiveDomain} limit`;
  } else if (!hasRequiredPermission) {
    lockedMessage = 'You do not have permission to perform this action';
  }
  
  // Simple logic: determine what to show
  let content: React.ReactNode = children;
  
  // Handle fallback or hide
  if (shouldShowLocked && fallback !== undefined) {
    content = fallback;
  } else if (shouldShowLocked && !showLocked) {
    content = null;
  }
  // PERMISSION/LIMIT RESTRICTION: Show lock + tooltip, navigate to upgrade
  else if (shouldShowLocked && React.isValidElement(children)) {
    content = (
      <div 
        ref={containerRef}
        className="relative inline-block group/locked"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          navigate('/upgrade');
        }}
      >
        {React.cloneElement(children, {
          ...children.props,
          disabled: true,
          className: `${children.props.className || ''} opacity-40 cursor-pointer`,
        })}
        <div className="absolute inset-0 backdrop-blur-sm bg-background/70 group-hover/locked:bg-background/80 rounded-md cursor-pointer transition-all">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-muted/80 group-hover/locked:scale-110 transition-transform">
              <Lock className="h-4 w-4 text-muted-foreground/90" />
            </div>
          </div>
        </div>
        {lockedMessage && (
          <div className={cn(
            "absolute px-4 py-3 bg-popover text-popover-foreground text-sm rounded-lg shadow-xl border-2 opacity-0 group-hover/locked:opacity-100 transition-all duration-200 z-[9999] min-w-[280px] max-w-[320px] pointer-events-none whitespace-normal",
            tooltipPosition === 'right' && "left-full ml-2 top-1/2 -translate-y-1/2",
            tooltipPosition === 'left' && "right-full mr-2 top-1/2 -translate-y-1/2",
            tooltipPosition === 'top' && "bottom-full mb-2 left-1/2 -translate-x-1/2",
            tooltipPosition === 'bottom' && "top-full mt-2 left-1/2 -translate-x-1/2"
          )}>
            <div className="flex flex-col gap-2">
              <p className="font-medium text-foreground">{lockedMessage}</p>
              {limitInfo && (
                <p className="text-xs text-muted-foreground">
                  {limitInfo.current} / {limitInfo.limit} {effectiveDomain} used
                </p>
              )}
              <div className="flex items-center gap-2 text-primary text-xs">
                <Crown className="h-3.5 w-3.5" />
                <span className="font-medium">Click to upgrade your plan</span>
              </div>
            </div>
            {tooltipPosition === 'right' && (
              <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-popover"></div>
            )}
            {tooltipPosition === 'left' && (
              <div className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-popover"></div>
            )}
            {tooltipPosition === 'top' && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-popover"></div>
            )}
            {tooltipPosition === 'bottom' && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-popover"></div>
            )}
          </div>
        )}
      </div>
    );
  }
  
  return <>{content}</>;
}

interface PermissionButtonProps extends React.ComponentProps<typeof Button> {
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
}

export function PermissionButton({
  permission,
  permissions,
  requireAll = false,
  disabled,
  ...props
}: PermissionButtonProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  
  let hasAccess = true;
  
  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  }
  
  return <Button {...props} disabled={disabled || !hasAccess} />;
}
